/**
 * Single contextual-sidebar config for the WHOLE AES Gateway service — both the
 * gateway ops console (`/services/aes/:id/*`) AND the self-hosted DRM sub-console
 * (`/services/aes/:id/drm/*`). Both render this same menu via `useAesContextualNav`
 * so the sidebar is ONE stable menu across the two surfaces instead of each route
 * swapping its own. Two groups keep the modes legible:
 *
 *   - 'AES Gateway'           gateway-wide ops via @mgmt / control plane — counts
 *                             EVERY key the gateway serves (CDN + self-hosted).
 *   - 'Self-hosted streams'   the DRM packager pipeline (sig-control), scoped to
 *                             the caller's own streams (BETA).
 *
 * Usage / Logs / Settings are SHARED single entries in the gateway group (Usage
 * carries an in-page Gateway-wide ↔ Self-hosted scope toggle; Logs' event dropdown
 * already spans gateway + DRM events) — no per-mode duplicates. The DRM group
 * carries only the stream-management surface unique to the self-hosted pipeline.
 */
import { createElement } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  KeyRound,
  Shuffle,
  BarChart3,
  ScrollText,
  Settings,
  Radio,
  type LucideIcon,
} from 'lucide-react';
import { useSetContextualNav } from '@/components/layout/nav-context';

export interface AesNavEntry {
  slug: string;
  label: string;
  icon: LucideIcon;
  /** Page-header subtitle for the gateway sub-pages (DRM pages render own headers). */
  description?: string;
}

export interface AesNavGroup {
  heading: string;
  items: AesNavEntry[];
}

export const AES_NAV_GROUPS: AesNavGroup[] = [
  {
    heading: 'AES Gateway',
    items: [
      // Single MERGED overview at the service root — gateway ops counters AND the
      // self-hosted data-plane health/streams on one page (no split).
      { slug: '', label: 'Overview', icon: LayoutDashboard, description: 'HLS AES-128 key gateway (VOD + LIVE) — gateway ops counters plus self-hosted stream health.' },
      { slug: 'sessions', label: 'Sessions', icon: KeyRound, description: 'Registered DRM sessions across the gateway — revoke to invalidate a key immediately.' },
      { slug: 'scrambler', label: 'Scrambler streams', icon: Shuffle, description: 'Stream whitelist for CDN Third Party Key Server mode. Issue a key to paste into the CDN form.' },
    ],
  },
  {
    heading: 'Self-hosted streams · BETA',
    items: [
      { slug: 'drm/streams', label: 'Sources & Streams', icon: Radio },
    ],
  },
  {
    // Shared surfaces used by BOTH modes — Usage carries an in-page scope toggle,
    // Logs' event dropdown spans gateway + DRM events, Settings is gateway-wide.
    heading: 'Shared',
    items: [
      { slug: 'usage', label: 'Business Usage', icon: BarChart3, description: 'Keys issued, active sessions and unique clients over a chosen period — toggle gateway-wide (datalake) or self-hosted per-stream (sig-control); export CSV.' },
      { slug: 'logs', label: 'Live logs', icon: ScrollText, description: 'Recent structured audit events across the gateway — filter to DRM key-delivery events in the dropdown.' },
      { slug: 'settings', label: 'Settings', icon: Settings, description: 'Control-plane API endpoint and the JWT-scoped routes for managing streams, sessions, usage and logs.' },
    ],
  },
];

/** Flat list of every entry, for lookups. */
export const AES_NAV_ENTRIES = AES_NAV_GROUPS.flatMap((g) => g.items);

/** Absolute href for an entry slug under the service base. */
export function aesHref(base: string, slug: string): string {
  return slug ? `${base}/${slug}` : base;
}

/** Active sidebar id = longest entry href that prefixes the current path (so
 *  `/drm/streams/vtv` → `drm/streams`, `/usage` → `usage` not `drm/usage`). */
export function aesActiveId(base: string, pathname: string): string {
  const matches = AES_NAV_ENTRIES.map((e) => aesHref(base, e.slug))
    .filter((href) => pathname === href || pathname.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? base;
}

/**
 * Drive the app ContextualSidebar with the unified AES menu. Called by BOTH
 * AesConsole and DrmConsole with the same config, so navigating between the
 * gateway and DRM surfaces keeps one stable sidebar (only the active item moves).
 */
export function useAesContextualNav(serviceId: string | undefined): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = `/services/aes/${serviceId}`;
  useSetContextualNav(
    () => ({
      sections: AES_NAV_GROUPS.map((g) => ({
        heading: g.heading,
        items: g.items.map((e) => ({
          id: aesHref(base, e.slug),
          label: e.label,
          icon: createElement(e.icon, { size: 16 }),
          onSelect: () => navigate(aesHref(base, e.slug)),
        })),
      })),
      activeId: aesActiveId(base, pathname),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, pathname, navigate],
  );
}
